import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Package2, ArrowDownLeft, ArrowUpRight, CheckCircle2, Truck, AlertTriangle, X as XIcon } from "lucide-react";
import api, { friendlyMessage } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { formatINR, formatDate, formatTime } from "../../lib/utils.fmt";
import { toast } from "sonner";
import EmptyState, { LoadingState } from "../../components/EmptyState";

const ACTION_META = {
  assigned: { Icon: ArrowDownLeft, color: "bg-brand-sky/10 text-brand-sky", label: "Assigned" },
  delivered: { Icon: Truck, color: "bg-brand-leaf/10 text-brand-leaf", label: "Delivered" },
  returned: { Icon: ArrowUpRight, color: "bg-brand-leaf/10 text-brand-leaf", label: "Returned" },
  damaged: { Icon: AlertTriangle, color: "bg-brand-clay/10 text-brand-clay", label: "Damaged" },
  lost: { Icon: AlertTriangle, color: "bg-brand-clay/10 text-brand-clay", label: "Lost" },
  pickup_requested: { Icon: Package2, color: "bg-brand-sand/10 text-brand-sand", label: "Pickup Requested" },
};

export default function CustomerBottleHistory() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = () => {
    setError(null);
    api.get(`/api/admin/customers/${id}/bottle-history`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(friendlyMessage(e)));
  };
  useEffect(() => { load(); }, [id]);

  const fulfill = async (rid) => {
    if (!window.confirm("Mark pickup as fulfilled? This will decrement the customer's counter.")) return;
    try {
      await api.patch(`/api/admin/bottle-pickup/${rid}/fulfill`);
      toast.success("Pickup fulfilled");
      load();
    } catch (e) {
      toast.error(friendlyMessage(e));
    }
  };

  if (error) {
    return (
      <EmptyState icon={AlertTriangle} title="Couldn't load bottle history" description={error}
        action={<button onClick={load} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>}
        fullHeight />
    );
  }
  if (!data) return <LoadingState fullHeight />;

  const c = data.customer;
  const openPickup = data.logs.find((l) => l.action === "pickup_requested" && l.status === "open");

  return (
    <div className="space-y-6 page-enter" data-testid="customer-bottle-history-page">
      <div className="flex items-center gap-3">
        <Link to="/admin/customers" className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center" data-testid="back-btn">
          <ChevronLeft className="w-5 h-5" />
        </Link>
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Customer Bottle History</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">{c.name}</h1>
        </div>
      </div>

      {/* Header card */}
      <div className="bg-white rounded-3xl border border-neutral-200 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5">
        <div className="w-16 h-16 rounded-3xl bg-brand-sky/10 text-brand-sky flex items-center justify-center font-heading font-extrabold text-2xl">
          {c.name?.[0]?.toUpperCase()}
        </div>
        <div className="flex-1">
          <div className="font-heading text-xl font-extrabold">{c.name}</div>
          <div className="text-sm text-neutral-600 font-mono">{c.phone}</div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Currently holds</div>
          <div className="font-heading text-4xl font-extrabold text-brand-sky mt-1">🍶 {c.bottles_held}</div>
        </div>
      </div>

      {/* Open pickup request */}
      {openPickup && (
        <div className="rounded-3xl bg-brand-sand/10 border-2 border-brand-sand/40 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="open-pickup-card">
          <Package2 className="w-7 h-7 text-brand-sand shrink-0" strokeWidth={2.2}/>
          <div className="flex-1">
            <div className="font-heading font-bold">Pickup requested · {openPickup.count} bottle{openPickup.count > 1 ? "s" : ""}</div>
            <div className="text-sm text-neutral-700">Requested {formatDate(openPickup.created_at)} at {formatTime(openPickup.created_at)}</div>
            {openPickup.notes && <div className="text-xs text-neutral-600 mt-1">Note: {openPickup.notes}</div>}
          </div>
          <Button onClick={() => fulfill(openPickup.id)} className="h-11 rounded-2xl bg-brand-leaf hover:bg-green-700 font-bold w-full sm:w-auto" data-testid="fulfill-btn">
            <CheckCircle2 className="w-4 h-4 mr-1" /> Mark fulfilled
          </Button>
        </div>
      )}

      {/* Timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-3xl border border-neutral-200 p-6">
          <h3 className="font-heading font-bold mb-4">Bottle log ({data.logs.length})</h3>
          <div className="relative pl-6 space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="absolute left-2 top-2 bottom-2 w-px bg-neutral-200" />
            {data.logs.length === 0 && <EmptyState icon={Package2} size="sm" title="No bottle activity yet" className="py-4" />}
            {data.logs.map((l) => {
              const m = ACTION_META[l.action] || ACTION_META.assigned;
              return (
                <div key={l.id} className="relative" data-testid={`log-${l.id}`}>
                  <div className={`absolute -left-6 top-1 w-4 h-4 rounded-full ${m.color} flex items-center justify-center`}>
                    <m.Icon className="w-2.5 h-2.5" />
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold text-sm flex items-center gap-2">
                        {m.label}
                        {l.count > 0 && <span className="text-brand-sky font-mono">×{l.count}</span>}
                        {l.status === "cancelled" && <span className="px-1.5 py-0.5 rounded text-[10px] uppercase bg-neutral-100 text-neutral-500">cancelled</span>}
                        {l.status === "fulfilled" && <span className="px-1.5 py-0.5 rounded text-[10px] uppercase bg-brand-leaf/10 text-brand-leaf">fulfilled</span>}
                      </div>
                      {l.notes && <div className="text-xs text-neutral-500 mt-0.5">{l.notes}</div>}
                    </div>
                    <div className="text-xs text-neutral-400 shrink-0 font-mono">{formatDate(l.created_at)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-neutral-200 p-6">
          <h3 className="font-heading font-bold mb-4">Delivered orders</h3>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {data.deliveries.length === 0 && <EmptyState icon={CheckCircle2} size="sm" title="No delivered orders yet" className="py-4" />}
            {data.deliveries.map((d) => (
              <div key={d.id} className="flex items-center gap-3 py-2 border-b last:border-b-0 border-neutral-100" data-testid={`delivery-${d.id}`}>
                <div className="w-9 h-9 rounded-2xl bg-brand-leaf/10 text-brand-leaf flex items-center justify-center">
                  <CheckCircle2 className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{d.product_name} × {d.quantity}</div>
                  <div className="text-xs text-neutral-500">{formatDate(d.delivery_date)} · {d.slot}</div>
                </div>
                <div className="text-right text-sm">
                  <div className="font-mono font-bold">{formatINR(d.amount)}</div>
                  {d.bottles_returned_count > 0 && <div className="text-xs text-brand-leaf font-bold">🍶 +{d.bottles_returned_count}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
